import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Network, Search, Upload, Send, RefreshCw, Trash2, CircleDot, X, HelpCircle, Wand2 } from "lucide-react";
import { PageHeader, PageBody } from "../components/PageScaffold";
import { Panel, Button, Pill, CenteredSpinner, ErrorBanner, EmptyState } from "../components/primitives";
import { WikiApi } from "../lib/api";
import { cn } from "../lib/cn";
import { useToast } from "../lib/toast";
import type { WikiQueueItem, WikiQueueStatus, WikiHit } from "../lib/types";

const STATUS_TONE: Record<WikiQueueStatus, "neutral" | "info" | "success" | "warning" | "danger"> = {
  queued: "neutral", processing: "info", done: "success", needs_review: "warning", failed: "danger",
};

export default function Wiki() {
  const qc = useQueryClient();
  const invalidate = () => { qc.invalidateQueries({ queryKey: ["wiki"] }); };
  const overview = useQuery({ queryKey: ["wiki", "overview"], queryFn: () => WikiApi.overview(), refetchInterval: 8000 });
  const [topic, setTopic] = useState<string | null>(null);

  return (
    <>
      <PageHeader title="Wiki" icon={Network} subtitle="Shared AYANA knowledge — agents query it for context; queue sources for the curator to ingest (all local models)" />
      <PageBody className="space-y-4">
        {overview.error && <ErrorBanner error={overview.error as Error} />}
        {overview.data && (
          <div className="flex flex-wrap gap-2 text-[11px]">
            <Pill tone="info">{overview.data.stats.entries} entries</Pill>
            <Pill tone="neutral">{overview.data.stats.topics} topics</Pill>
            <Pill tone="neutral">{overview.data.stats.embedded} embedded</Pill>
            <Pill tone={overview.data.queue.queued + overview.data.queue.processing ? "warning" : "neutral"}>
              queue: {overview.data.queue.queued} queued · {overview.data.queue.processing} running
            </Pill>
            {overview.data.queue.needs_review > 0 && <Pill tone="warning">{overview.data.queue.needs_review} need review</Pill>}
            {overview.data.queue.failed > 0 && <Pill tone="danger">{overview.data.queue.failed} failed</Pill>}
            {overview.data.search && overview.data.search.searches > 0 && (
              <Pill tone={overview.data.search.misses > 0 ? "warning" : "neutral"}>
                {overview.data.search.searches} searches · {overview.data.search.misses} missed
              </Pill>
            )}
            <Pill tone={overview.data.spark ? "success" : "danger"}>
              <CircleDot className="size-3" /> Spark {overview.data.spark ? "online" : "offline"}
            </Pill>
          </div>
        )}

        <SearchPanel />
        {topic && <TopicPanel topic={topic} onClose={() => setTopic(null)} />}
        <AddPanel onChanged={invalidate} />
        <GapsPanel />
        <LintPanel />
        <QueuePanel onChanged={invalidate} />
        {overview.data && overview.data.topics.length > 0 && (
          <Panel className="p-4 space-y-2">
            <div className="text-[11px] uppercase tracking-wide text-fg-subtle">Topics ({overview.data.topics.length}) — click to browse</div>
            <div className="flex flex-wrap gap-1.5">
              {overview.data.topics.map((t) => (
                <button key={t.topic} onClick={() => setTopic(t.topic)}>
                  <Pill tone={topic === t.topic ? "info" : "neutral"}>{t.topic} · {t.count}</Pill>
                </button>
              ))}
            </div>
          </Panel>
        )}
      </PageBody>
    </>
  );
}

function SearchPanel() {
  const [q, setQ] = useState("");
  const [submitted, setSubmitted] = useState("");
  const search = useQuery({
    queryKey: ["wiki", "search", submitted],
    queryFn: () => WikiApi.search(submitted),
    enabled: submitted.trim().length > 0,
  });
  return (
    <Panel className="p-4 space-y-3">
      <div className="text-sm font-semibold text-fg">Query the wiki</div>
      <form className="flex items-center gap-2" onSubmit={(e) => { e.preventDefault(); setSubmitted(q); }}>
        <div className="relative flex-1">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 size-3.5 text-fg-subtle pointer-events-none" />
          <input value={q} onChange={(e) => setQ(e.target.value)} className="w-full text-sm pl-7" placeholder="e.g. forbes 5-star digital requirements, voucher template…" />
        </div>
        <Button size="sm" type="submit" disabled={!q.trim()}>Search</Button>
      </form>
      {search.isFetching && <CenteredSpinner label="searching" />}
      {search.error && <ErrorBanner error={search.error as Error} />}
      {search.data && submitted && (
        search.data.hits.length === 0
          ? <p className="text-[11px] text-fg-subtle">No matches — the wiki may not hold this yet. Queue a source below.</p>
          : <div className="space-y-2">{search.data.hits.map((h, i) => <HitRow key={i} h={h} />)}</div>
      )}
      {!submitted && <p className="text-[11px] text-fg-subtle">Same search agents run via <code className="font-mono">wiki_search</code>.</p>}
    </Panel>
  );
}

function HitRow({ h }: { h: WikiHit }) {
  return (
    <div className="rounded-md border border-border/60 bg-surface-2/40 p-2.5 space-y-1">
      <p className="text-xs text-fg leading-relaxed">{h.subject && <span className="font-mono text-accent">[{h.subject}] </span>}{h.content}</p>
      <div className="flex flex-wrap items-center gap-1.5 text-[10px] text-fg-subtle">
        {h.topics.map((t) => <span key={t} className="rounded bg-surface-elevated px-1.5 py-0.5 font-mono">{t}</span>)}
        {h.source && <span className="ml-auto font-mono">{h.source}</span>}
        <span>· ingested {h.ingested_at.slice(0, 16).replace("T", " ")}</span>
      </div>
    </div>
  );
}

function TopicPanel({ topic, onClose }: { topic: string; onClose: () => void }) {
  const { data, isLoading, error } = useQuery({ queryKey: ["wiki", "topic", topic], queryFn: () => WikiApi.topic(topic) });
  const entries = data?.entries ?? [];
  const overview = entries.find((e) => e.kind === "overview");
  const facts = entries.filter((e) => e.kind !== "overview");
  return (
    <Panel className="p-4 space-y-3">
      <div className="flex items-center gap-2">
        <div className="text-sm font-semibold text-fg">Topic <span className="font-mono text-accent">{topic}</span></div>
        {!isLoading && <span className="text-[11px] text-fg-subtle">{facts.length} entr{facts.length === 1 ? "y" : "ies"}</span>}
        <button onClick={onClose} className="ml-auto text-fg-subtle hover:text-fg" title="Close"><X className="size-4" /></button>
      </div>
      {isLoading && <CenteredSpinner label="loading topic" />}
      {error && <ErrorBanner error={error as Error} />}
      {overview && (
        <div className="rounded-md border border-accent/40 bg-accent/5 p-2.5">
          <div className="text-[10px] uppercase tracking-wide text-accent mb-1">Synthesized overview</div>
          <p className="text-xs text-fg leading-relaxed">{overview.content}</p>
        </div>
      )}
      {facts.length > 0
        ? <div className="space-y-2">{facts.map((h, i) => <HitRow key={i} h={h} />)}</div>
        : !isLoading && <p className="text-[11px] text-fg-subtle">No entries under this topic.</p>}
    </Panel>
  );
}

function GapsPanel() {
  const { data, isLoading, error } = useQuery({ queryKey: ["wiki", "gaps"], queryFn: () => WikiApi.gaps(), refetchInterval: 15000 });
  const gaps = data?.gaps ?? [];
  return (
    <Panel className="p-4 space-y-3">
      <div className="flex items-center gap-2">
        <HelpCircle className="size-4 text-warning" />
        <div className="text-sm font-semibold text-fg">Knowledge gaps</div>
        <span className="text-[11px] text-fg-subtle">questions agents asked that the wiki couldn't answer — ingest these next</span>
      </div>
      {isLoading && <CenteredSpinner label="loading gaps" />}
      {error && <ErrorBanner error={error as Error} />}
      {data && gaps.length === 0 && <p className="text-[11px] text-fg-subtle">No misses yet — every wiki_search so far found something.</p>}
      {gaps.length > 0 && (
        <div className="space-y-1.5">
          {gaps.map((g, i) => (
            <div key={i} className="rounded-md border border-border/60 bg-surface-2/30 p-2.5 flex items-center gap-2 text-[11px]">
              <Pill tone="warning">{g.misses}×</Pill>
              <span className="text-fg flex-1 leading-relaxed">{g.query}</span>
              {g.actors && <span className="text-fg-subtle font-mono">{g.actors}</span>}
              <span className="text-fg-subtle">· {g.last_seen.slice(0, 16).replace("T", " ")}</span>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}

function LintRow({ id, content, onDelete, busy }: { id: number; content: string; onDelete: () => void; busy: boolean }) {
  return (
    <div className="rounded-md border border-border/60 bg-surface-2/30 p-2 flex items-center gap-2 text-[11px]">
      <span className="text-fg-subtle font-mono">#{id}</span>
      <span className="flex-1 text-fg">{content}</span>
      <button disabled={busy} onClick={onDelete} className="text-fg-subtle hover:text-danger" title="Delete entry"><Trash2 className="size-3.5" /></button>
    </div>
  );
}

function LintPanel() {
  const qc = useQueryClient();
  const toast = useToast();
  const [run, setRun] = useState(false);
  const { data, isFetching, error, refetch } = useQuery({ queryKey: ["wiki", "lint"], queryFn: () => WikiApi.lint(), enabled: run });
  const del = useMutation({
    mutationFn: (id: number) => WikiApi.deleteEntry(id),
    onSuccess: () => { toast.success("Entry deleted"); refetch(); qc.invalidateQueries({ queryKey: ["wiki", "overview"] }); },
    onError: (e) => toast.error("Delete failed", String((e as Error).message ?? e)),
  });
  const total = data ? data.nearDuplicates.length + data.orphans.length + data.tiny.length : 0;
  return (
    <Panel className="p-4 space-y-3">
      <div className="flex items-center gap-2">
        <Wand2 className="size-4 text-accent" />
        <div className="text-sm font-semibold text-fg">Curation / lint</div>
        <span className="text-[11px] text-fg-subtle hidden sm:inline">near-duplicates · orphans (no topics) · tiny entries</span>
        <Button size="sm" variant="secondary" className="ml-auto" onClick={() => { setRun(true); refetch(); }} loading={isFetching}>Run lint</Button>
      </div>
      {error && <ErrorBanner error={error as Error} />}
      {data && total === 0 && <p className="text-[11px] text-fg-subtle">Clean — no near-duplicates, orphans, or tiny entries.</p>}
      {data && data.nearDuplicates.length > 0 && (
        <div className="space-y-1.5">
          <div className="text-[11px] uppercase tracking-wide text-fg-subtle">Near-duplicates ({data.nearDuplicates.length})</div>
          {data.nearDuplicates.map((p, i) => (
            <div key={i} className="rounded-md border border-border/60 bg-surface-2/30 p-2.5 space-y-1.5 text-[11px]">
              <Pill tone="warning">distance {p.distance}</Pill>
              <LintRow id={p.a} content={p.aContent} onDelete={() => del.mutate(p.a)} busy={del.isPending} />
              <LintRow id={p.b} content={p.bContent} onDelete={() => del.mutate(p.b)} busy={del.isPending} />
            </div>
          ))}
        </div>
      )}
      {data && data.orphans.length > 0 && (
        <div className="space-y-1.5">
          <div className="text-[11px] uppercase tracking-wide text-fg-subtle">Orphans — no topics ({data.orphans.length})</div>
          {data.orphans.map((o) => <LintRow key={o.id} id={o.id} content={o.content} onDelete={() => del.mutate(o.id)} busy={del.isPending} />)}
        </div>
      )}
      {data && data.tiny.length > 0 && (
        <div className="space-y-1.5">
          <div className="text-[11px] uppercase tracking-wide text-fg-subtle">Tiny entries ({data.tiny.length})</div>
          {data.tiny.map((o) => <LintRow key={o.id} id={o.id} content={o.content} onDelete={() => del.mutate(o.id)} busy={del.isPending} />)}
        </div>
      )}
    </Panel>
  );
}

function AddPanel({ onChanged }: { onChanged: () => void }) {
  const toast = useToast();
  const [kind, setKind] = useState<"text" | "url" | "asana">("text");
  const [payload, setPayload] = useState("");
  const [note, setNote] = useState("");
  const [topics, setTopics] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const parseTopics = () => topics.split(",").map((t) => t.trim()).filter(Boolean);

  const submit = useMutation({
    mutationFn: async () => {
      const r = await WikiApi.submit({ kind, payload: payload.trim(), note: note.trim() || undefined, topics: parseTopics() });
      if (r.error) throw new Error(r.error);
      return r;
    },
    onSuccess: (r) => { toast.success(`Queued #${r.id} for ingest`); setPayload(""); setNote(""); onChanged(); },
    onError: (e) => toast.error("Submit failed", String((e as Error).message ?? e)),
  });
  const upload = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error("pick a file");
      const fd = new FormData();
      fd.set("file", file);
      if (note.trim()) fd.set("note", note.trim());
      if (parseTopics().length) fd.set("topics", parseTopics().join(","));
      const r = await WikiApi.upload(fd);
      if (r.error) throw new Error(r.error);
      return r;
    },
    onSuccess: (r) => { toast.success(`Uploaded + queued #${r.id}`); setFile(null); onChanged(); },
    onError: (e) => toast.error("Upload failed", String((e as Error).message ?? e)),
  });

  return (
    <Panel className="p-4 space-y-3">
      <div className="text-sm font-semibold text-fg">Add knowledge</div>
      <div className="grid sm:grid-cols-[140px_1fr] gap-2">
        <label className="text-xs"><span className="block text-fg-muted mb-0.5">Kind</span>
          <select value={kind} onChange={(e) => setKind(e.target.value as "text" | "url" | "asana")} className="w-full text-sm h-8">
            <option value="text">Text</option><option value="url">URL</option><option value="asana">Asana task</option>
          </select></label>
        <label className="text-xs"><span className="block text-fg-muted mb-0.5">{kind === "text" ? "Text" : kind === "url" ? "URL" : "Asana task GID or link"}</span>
          {kind === "text"
            ? <textarea value={payload} onChange={(e) => setPayload(e.target.value)} rows={3} className="w-full text-sm" placeholder="Paste the knowledge…" />
            : <input value={payload} onChange={(e) => setPayload(e.target.value)} className="w-full text-sm font-mono" placeholder={kind === "url" ? "https://…" : "1215615859727759"} />}
        </label>
      </div>
      <div className="grid sm:grid-cols-2 gap-2">
        <label className="text-xs"><span className="block text-fg-muted mb-0.5">Note / hint (optional)</span>
          <input value={note} onChange={(e) => setNote(e.target.value)} className="w-full text-sm" placeholder="e.g. this is the call-a-buggy spec" /></label>
        <label className="text-xs"><span className="block text-fg-muted mb-0.5">Suggested topics (comma-sep, optional)</span>
          <input value={topics} onChange={(e) => setTopics(e.target.value)} className="w-full text-sm font-mono" placeholder="ayana-app, booking-engine" /></label>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" onClick={() => submit.mutate()} loading={submit.isPending} disabled={!payload.trim()}><Send className="size-3.5" /> Queue {kind}</Button>
        <span className="text-fg-subtle text-[11px]">— or —</span>
        <label className="inline-flex items-center gap-2 text-[11px] text-fg-muted cursor-pointer">
          <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.gif,.mp3,.m4a,.wav,.ogg,.opus" className="hidden" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
          <span className="rounded-md border border-border px-2 py-1 hover:bg-surface-elevated">{file ? file.name : "Choose file (PDF/image/audio)"}</span>
        </label>
        {file && <Button size="sm" variant="secondary" onClick={() => upload.mutate()} loading={upload.isPending}><Upload className="size-3.5" /> Upload + queue</Button>}
      </div>
    </Panel>
  );
}

function QueuePanel({ onChanged }: { onChanged: () => void }) {
  const toast = useToast();
  const { data, isLoading, error } = useQuery({ queryKey: ["wiki", "queue"], queryFn: () => WikiApi.queue(), refetchInterval: 5000 });
  const retry = useMutation({ mutationFn: (id: number) => WikiApi.retry(id), onSuccess: () => { toast.success("Re-queued"); onChanged(); }, onError: (e) => toast.error("Retry failed", String(e)) });
  const cancel = useMutation({ mutationFn: (id: number) => WikiApi.cancel(id), onSuccess: () => { toast.success("Removed"); onChanged(); }, onError: (e) => toast.error("Cancel failed", String(e)) });

  return (
    <Panel className="p-4 space-y-3">
      <div className="text-sm font-semibold text-fg">Ingest queue</div>
      {isLoading && <CenteredSpinner label="loading queue" />}
      {error && <ErrorBanner error={error as Error} />}
      {data && data.items.length === 0 && <EmptyState icon={<Network className="size-7" />} title="Queue empty" description="Add a source above — agents also contribute via wiki_submit." />}
      {data && data.items.length > 0 && (
        <div className="space-y-1.5">
          {data.items.map((it) => <QueueRow key={it.id} it={it} onRetry={() => retry.mutate(it.id)} onCancel={() => cancel.mutate(it.id)} busy={retry.isPending || cancel.isPending} />)}
        </div>
      )}
    </Panel>
  );
}

function QueueRow({ it, onRetry, onCancel, busy }: { it: WikiQueueItem; onRetry: () => void; onCancel: () => void; busy: boolean }) {
  const preview = it.kind === "text" ? it.payload.slice(0, 80) : it.payload;
  return (
    <div className="rounded-md border border-border/60 bg-surface-2/30 p-2.5">
      <div className="flex items-center gap-2 flex-wrap text-[11px]">
        <Pill tone={STATUS_TONE[it.status]}>{it.status}</Pill>
        <Pill tone="neutral">{it.kind}</Pill>
        <span className="font-mono text-fg-muted truncate max-w-[45%]" title={it.payload}>{preview}</span>
        <span className="text-fg-subtle">· {it.requested_by}</span>
        <span className="ml-auto" />
        {(it.status === "failed" || it.status === "needs_review") && <button disabled={busy} onClick={onRetry} className="inline-flex items-center gap-1 text-fg-subtle hover:text-fg" title="Re-queue"><RefreshCw className="size-3.5" /></button>}
        {(it.status === "queued" || it.status === "failed" || it.status === "needs_review") && <button disabled={busy} onClick={onCancel} className="text-fg-subtle hover:text-danger" title="Remove"><Trash2 className="size-3.5" /></button>}
      </div>
      {(it.result || it.error) && <p className={cn("text-[10px] mt-1", it.error ? "text-danger" : "text-fg-subtle")}>{it.error || it.result}</p>}
    </div>
  );
}
